import { useState } from 'react';
import { formatMoney, type Assignment } from '@teentack/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ErrorNote, InfoNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';
import { useMoneySummary } from '@/features/payments/use-payments';
import { useApproveAssignment, useReleasePayment } from './use-assignments';

/**
 * The employer's end of the completion loop.
 * [Job Poster steps 26-28 and 31]
 *
 * Three separate acts, deliberately not collapsed into one button: the teen
 * says it is finished, the employer agrees it is finished, and the employer
 * pays. Someone can approve today and pay tomorrow, and approving does not
 * quietly move money — which is the whole reason the api keeps them apart.
 */
export function AssignmentActions({ assignment }: { assignment: Assignment }) {
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const approve = useApproveAssignment();
  const release = useReleasePayment();
  // Whether pressing Release actually takes money depends on the gateway the
  // server booted with, so the button says which it is.
  const money = useMoneySummary();
  const settles = money.data?.settlesRealMoney ?? false;

  async function run(action: () => Promise<unknown>, success: string): Promise<void> {
    setError(null);
    setNotice(null);
    try {
      await action();
      setNotice(success);
    } catch (caught) {
      setError(presentApiError(caught, 'Something went wrong').message);
    }
  }

  const pay = formatMoney(assignment.agreedPay.amountMinor, assignment.agreedPay.currency);
  const awaitingApproval = assignment.status === 'COMPLETED';
  const awaitingPayment =
    assignment.status === 'APPROVED' && assignment.paymentStatus === 'UNPAID';
  const failed = assignment.paymentStatus === 'FAILED';

  // Nothing for the employer to do at this point in the job's life.
  if (!awaitingApproval && !awaitingPayment && !failed) {
    if (assignment.paymentStatus === 'RELEASED') {
      return (
        <p className="rounded-xl border border-safe-200 bg-safe-50 px-4 py-3 text-sm font-medium text-safe-700">
          Approved and paid — {pay}.
        </p>
      );
    }
    return null;
  }

  return (
    <div className="space-y-3">
      {error && <ErrorNote message={error} />}
      {notice && <InfoNote>{notice}</InfoNote>}

      {awaitingApproval && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-sun-200 bg-sun-50 px-4 py-3">
          <p className="flex-1 text-sm font-medium text-sun-800">
            {assignment.teen.name} marked this finished. Approve it to close the work off.
          </p>
          <Button
            variant="success"
            disabled={approve.isPending}
            onClick={() =>
              void run(
                () => approve.mutateAsync(assignment.id),
                'Approved. You can release the payment when you are ready.',
              )
            }
          >
            {approve.isPending ? 'Approving…' : 'Approve the work'}
          </Button>
        </div>
      )}

      {awaitingPayment && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-brand-800">
              Approved. {pay} is still to be released.
            </p>
            <p className="mt-0.5 text-xs text-brand-700">
              {settles
                ? 'This charges your default payment method.'
                : 'No payment provider is connected, so this records the payment rather than taking it.'}
            </p>
          </div>
          <Button
            disabled={release.isPending}
            onClick={() =>
              void run(() => release.mutateAsync(assignment.id), `Payment of ${pay} released.`)
            }
          >
            {release.isPending ? 'Releasing…' : `Release ${pay}`}
          </Button>
        </div>
      )}

      {failed && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <p className="flex-1 text-sm font-medium text-red-800">
            The last payment attempt failed. Check your payment method and try again.
          </p>
          <Badge tone="sun">Failed</Badge>
          <Button
            variant="outline"
            disabled={release.isPending}
            onClick={() =>
              void run(() => release.mutateAsync(assignment.id), `Payment of ${pay} released.`)
            }
          >
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}
